import * as React from "react";
import { Location, ChevronRight, Close } from "../../components/Icons";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId
} from "react-google-places-autocomplete";
import StoreMap from "./StoreMap";
import { autocompleteConfig, configLocation } from "./fisConfig";
import { getDistance } from "../../utils";

interface IProps {
  variant: {
    availableForSale: boolean;
    id: number;
    options: {
      name: string;
      value: string;
    }[];
    price: number;
    title: string;
  };
  stores: any;
}

interface Store {
  address: string;
  direction: string;
  distance: number;
  hours: string;
  image: string;
  name: string;
  phone: string;
  status: string;
  latitude: number;
  longitude: number;
}

type LatLng = {
  lat: number;
  lng: number;
};

const FindInStore: React.FC<IProps> = ({ stores }) => {
  let [loading, setLoading] = React.useState(false);
  let [stocked, setStocked] = React.useState(stores);
  let [mapOpen, setMapOpen] = React.useState(false);
  let [activeStore, setActiveStore] = React.useState(null);

  // this is a bit hacky but I couldn't find a simpler way to trigger the close
  const handleClose = () => {
    const overlay = document.querySelector(".modal__overlay");
    if (overlay instanceof HTMLElement) {
      overlay.click();
    }
  };

  const handlePlace = React.useCallback(async (place: any) => {
    if (place === null) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    filterLocations({ lat, lng });
  }, []);

  const calcDistance = ({ lat, lng }: LatLng) => {
    stores.map((store: Store) => {
      const distance = getDistance(
        lat,
        lng,
        store.latitude,
        store.longitude,
        "K"
      );

      setStocked((prevStocked: any) => {
        return prevStocked.map((store: any) => {
          store.distance = distance;
          return store;
        });
      });
    });
  };

  const filterLocations = ({ lat, lng }: LatLng) => {
    const searchRadius: number = 50;

    const filtered = stores
      .filter(
        (store: Store): Boolean => {
          const distance = getDistance(
            lat,
            lng,
            store.latitude,
            store.longitude,
            "K"
          );
          store.distance = distance;

          return distance < searchRadius;
        }
      )
      .sort((a: Store, b: Store) => a.distance - b.distance);

    setStocked(filtered);
  };

  // filter stores when a search suggestion is clicked
  const handleSelection = React.useCallback(
    async (event, suggestion, onSelectSuggestion) => {
      onSelectSuggestion(suggestion, event);
      const place = await geocodeByPlaceId(suggestion?.place_id);
      handlePlace(place?.[0]);
    },
    [handlePlace]
  );

  // Filter stores based on geolocation of user
  const handleGeolocation = React.useCallback(async () => {
    navigator.geolocation.getCurrentPosition((position: any) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      filterLocations({ lat, lng });
      calcDistance({ lat, lng });
    });
  }, [configLocation]);

  // check stock against dotapparel21 API
  const checkStockLevels = React.useCallback(async () => {
    const sku: any = document.querySelector("select[title=Variant] [selected]");

    await fetch(`/apps/dotapparel21/api-checkStock?sku=${sku.dataset.sku}`)
      .then(async response => await response.json())
      .then(data => {
        const ap21StoresArray: any = Object.values(data)[0];

        stores.map((store: any, index: number) => {
          const storeName = getStoreName(store.name)

          const ap21Store = ap21StoresArray.find((currentStore: any) => {
            return (
              currentStore.attributes.storeName.trim() === storeName ||
              store.name.includes(currentStore.attributes.storeName.trim())
            );
          });

          if (ap21Store) {
            const stockBuffer = 2;
            switch (true) {
              case ap21Store.attributes.freeStock < stockBuffer:
                setStocked((prevStocked: any) => {
                  let newData = [...prevStocked];
                  newData[index].status =
                    "Low stock. Please call store to confirm";
                  return newData;
                });
                break;

              case ap21Store.attributes.freeStock >= stockBuffer:
                setStocked((prevStocked: any) => {
                  let newData = [...prevStocked];
                  newData[index].status = "In Stock";
                  return newData;
                });
                break;

              default:
                setStocked((prevStocked: any) => {
                  let newData = [...prevStocked];
                  newData[index].status = "Out of Stock";
                  return newData;
                });
                break;
            }
          } else {
            setStocked((prevStocked: any) => {
              let newData = [...prevStocked];
              newData[index].status = "Out of Stock";
              return newData;
            });
          }
        });
      })
      .catch(error => {
        console.warn(error);
      });
  }, [stocked, setLoading]);

  // Set active store and open map
  const handleStoreMap = (index: number) => {
    setActiveStore(stocked[index]);
    setMapOpen(true);
  };

  const handleInputEnter = (event: any) => {
    if (event.target.value.length === 0) {
      setStocked(stores);
    }
  };

  const getStoreName = (name: string): string => {
    const [first, second] = name.trim().split("- ")
    return (second || first).trim()
  }

  React.useEffect(() => {
    checkStockLevels();

    document.querySelectorAll(".ProductForm__Option").forEach((option: any) => {
      option.addEventListener("click", () => {
        setTimeout(() => checkStockLevels(), 100);
      });
    });

    return document
      .querySelectorAll(".ProductForm__Option")
      .forEach((option: any) => {
        option.removeEventListener("click", () => {
          checkStockLevels();
        });
      });
  }, []);

  function serialize(obj) {
    if (obj.type === "root") {
      return obj.children.map(child => serialize(child)).join("\n");
    }
    if (obj.type === "paragraph") {
      const text = obj.children.map(child => serialize(child)).join(" ");
      return "<p>" + text + "</p>";
    }
    if (obj.type === "text") {
      return obj.value;
    }
    return "";
  }


  return (
    <>
      <div
        className={`modal__overlay ${mapOpen ? "mapOpen transparent-background" : ""}`}
        tabIndex={-1}
        data-micromodal-close
      >
        <div
          className="modal__container full-width-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-1-title"
        >
          <header className="modal__header">
            <h2 className="modal__title" id="modal-1-title">
              Find in store
            </h2>
            <button
              className="modal__close"
              aria-label="Close modal"
              data-micromodal-close
              onClick={handleClose}
            >
              <Close width={14} />
            </button>
          </header>
          <main className="modal__content" id="find-in-store-modal-content">
            <div className="modal__actions">
              <button
                onClick={handleGeolocation}
                className="flex justify-center items-center gap-2 button primary uppercase px-4 font-bold"
              >
                <Location width={18} /> Use my current location
              </button>
              <GooglePlacesAutocomplete
                {...autocompleteConfig}
                loader={<></>}
                renderInput={(props: any) => {
                  return (
                    <input
                      {...props}
                      className="autocomplete-search border rounded-none"
                      autoComplete="street-address"
                      type="search"
                      placeholder={"Enter your suburb or postcode"}
                      onKeyUp={handleInputEnter}
                    />
                  );
                }}
                renderSuggestions={(
                  _: any,
                  suggestions: any,
                  onSelectSuggestion: any
                ) => {
                  return (
                    <>
                      {suggestions?.length > 0 && (
                        <ul className="suggestion__list absolute justify-start">
                          {suggestions.map((suggestion: any) => (
                            <li
                              className="suggestion__list-item cursor-pointer"
                              key={suggestion.place_id}
                              onClick={event =>
                                handleSelection(
                                  event,
                                  suggestion,
                                  onSelectSuggestion
                                )
                              }
                            >
                              {suggestion.description}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  );
                }}
              />
            </div>
            <div className="modal__store-list">
              {stocked.map((store: any, index: number) => {
                return (
                  <div key={index} className="modal__store-list-item">
                    <div className="w-1/4 pr-6 flex justify-center items-center sm:hidden">
                      <svg
                        className="icon icon-logo fill-current"
                        viewBox="0 0 220 53"
                        width="220"
                        height="53"
                      >
                        <defs>
                          <mask
                            id="mask"
                            x="198.79"
                            y="1.05"
                            width="21.18"
                            height="32.85"
                            maskUnits="userSpaceOnUse"
                          >
                            <g id="mask-2">
                              <polygon
                                id="path-1"
                                className="cls-1"
                                points="198.79 1.05 219.97 1.05 219.97 33.9 198.79 33.9 198.79 1.05"
                              />
                            </g>
                          </mask>
                          <mask
                            id="mask-2-2"
                            x="0"
                            y="0.54"
                            width="31.9"
                            height="33.74"
                            maskUnits="userSpaceOnUse"
                          >
                            <g id="mask-4">
                              <polygon
                                id="path-3"
                                className="cls-1"
                                points="0 0.54 31.91 0.54 31.91 34.27 0 34.27 0 0.54"
                              />
                            </g>
                          </mask>
                          <mask
                            id="mask-3"
                            x="106.04"
                            y="44.31"
                            width="8.67"
                            height="8.54"
                            maskUnits="userSpaceOnUse"
                          >
                            <g id="mask-6">
                              <polygon
                                id="path-5"
                                className="cls-1"
                                points="106.04 44.31 114.71 44.31 114.71 52.85 106.04 52.85 106.04 44.31"
                              />
                            </g>
                          </mask>
                          <mask
                            id="mask-4-2"
                            x="129.21"
                            y="44.44"
                            width="7.35"
                            height="8.4"
                            maskUnits="userSpaceOnUse"
                          >
                            <g id="mask-8">
                              <polygon
                                id="path-7"
                                className="cls-1"
                                points="129.21 44.44 136.56 44.44 136.56 52.85 129.21 52.85 129.21 44.44"
                              />
                            </g>
                          </mask>
                        </defs>
                        <title>logo</title>
                        <g id="UI-Kit">
                          <g id="Cable-UI-Kit">
                            <g id="Logo_Positive" data-name="Logo/Positive">
                              <g id="Group-37">
                                <path
                                  id="Fill-1"
                                  className="cls-2"
                                  d="M59.79,23.55,66.61,5.24l6.83,18.31ZM77.3,33.9h5L69.48,1.05H63.8L50.89,33.9h5l2.36-6.33H74.94Z"
                                />
                                <g id="Group-36">
                                  <path
                                    id="Fill-2"
                                    className="cls-2"
                                    d="M126.35,24.59c0-3-2.12-5.66-6.22-5.66H107.42V30.26h12.71c3.9,0,6.22-2.17,6.22-5.67ZM125.7,10c0-3-2-5.32-5.82-5.32H107.42V15.28h12.46c3.79,0,5.82-2.26,5.82-5.26ZM103.22,33.9V1.05h17.36c5.87,0,9.47,3.45,9.47,8.37A7.52,7.52,0,0,1,124.13,17,8.25,8.25,0,0,1,130.7,25c0,5.27-3.59,8.86-9.76,8.86Z"
                                  />
                                  <polygon
                                    id="Fill-4"
                                    className="cls-2"
                                    points="156.09 33.9 156.09 1.05 160.28 1.05 160.28 30.26 177.37 30.26 177.37 33.9 156.09 33.9"
                                  />
                                  <g id="Group-8">
                                    <g className="cls-3">
                                      <polygon
                                        id="Fill-6"
                                        className="cls-2"
                                        points="198.79 33.9 198.79 1.05 219.97 1.05 219.97 4.7 202.99 4.7 202.99 15.29 215.64 15.29 215.64 18.93 202.99 18.93 202.99 30.26 219.97 30.26 219.97 33.9 198.79 33.9"
                                      />
                                    </g>
                                  </g>
                                  <g id="Group-11">
                                    <g className="cls-4">
                                      <path
                                        id="Fill-9"
                                        className="cls-2"
                                        d="M17.33,30a12.83,12.83,0,0,1-13-12.64,12.83,12.83,0,0,1,13-12.64,13.08,13.08,0,0,1,11,6L31.9,8.3A17.41,17.41,0,0,0,17.33.54,17.11,17.11,0,0,0,0,17.4,17.12,17.12,0,0,0,17.33,34.27a17.43,17.43,0,0,0,14.54-7.71L28.3,24.15a13.05,13.05,0,0,1-11,5.89"
                                      />
                                    </g>
                                  </g>
                                  <polygon
                                    id="Fill-12"
                                    className="cls-2"
                                    points="18.42 17.48 18.37 17.44 18.32 17.48 18.42 17.48"
                                  />
                                  <polygon
                                    id="Fill-14"
                                    className="cls-2"
                                    points="18.37 17.44 18.43 17.4 18.32 17.4 18.37 17.44"
                                  />
                                  <polygon
                                    id="Fill-16"
                                    className="cls-2"
                                    points="28.82 52.7 28.82 48.15 26.46 52.7 25.36 52.7 22.98 48.15 22.98 52.7 21.5 52.7 21.5 44.44 23.05 44.44 25.91 50.34 28.76 44.44 30.31 44.44 30.31 52.7 28.82 52.7"
                                  />
                                  <polygon
                                    id="Fill-18"
                                    className="cls-2"
                                    points="45.18 52.7 45.18 44.44 50.52 44.44 50.52 45.72 46.67 45.72 46.67 47.85 49.5 47.85 49.5 49.12 46.67 49.12 46.67 51.42 50.52 51.42 50.52 52.7 45.18 52.7"
                                  />
                                  <polygon
                                    id="Fill-20"
                                    className="cls-2"
                                    points="194.49 52.7 194.49 44.44 199.84 44.44 199.84 45.72 195.98 45.72 195.98 47.85 198.81 47.85 198.81 49.12 195.98 49.12 195.98 51.42 199.84 51.42 199.84 52.7 194.49 52.7"
                                  />
                                  <polygon
                                    id="Fill-22"
                                    className="cls-2"
                                    points="65.49 52.7 65.49 44.44 66.97 44.44 66.97 51.42 70.71 51.42 70.71 52.7 65.49 52.7"
                                  />
                                  <path
                                    id="Fill-24"
                                    className="cls-2"
                                    d="M90.47,50.27a1.15,1.15,0,0,0-1.28-1.15H86.27v2.3h2.92a1.12,1.12,0,0,0,1.28-1.15Zm-.16-3.49a1.06,1.06,0,0,0-1.19-1.06H86.27v2.13h2.85a1.05,1.05,0,0,0,1.19-1.07ZM84.78,52.7V44.44h4.64a2.14,2.14,0,0,1,2.41,2.11,1.87,1.87,0,0,1-1.47,1.89,2,2,0,0,1,1.64,2,2.2,2.2,0,0,1-2.47,2.24Z"
                                  />
                                  <g id="Group-28">
                                    <g className="cls-5">
                                      <path
                                        id="Fill-26"
                                        className="cls-2"
                                        d="M113.19,48.58a2.81,2.81,0,1,0-2.81,3,2.78,2.78,0,0,0,2.81-3m-7.15,0a4.34,4.34,0,1,1,4.34,4.27A4.15,4.15,0,0,1,106,48.58"
                                      />
                                    </g>
                                  </g>
                                  <g id="Group-31">
                                    <g className="cls-6">
                                      <path
                                        id="Fill-29"
                                        className="cls-2"
                                        d="M129.21,49.43v-5h1.51v5a2.16,2.16,0,1,0,4.32,0v-5h1.52v5c0,2-1.21,3.42-3.68,3.42s-3.67-1.38-3.67-3.42"
                                      />
                                    </g>
                                  </g>
                                  <path
                                    id="Fill-32"
                                    className="cls-2"
                                    d="M156.88,47a1.31,1.31,0,0,0-1.45-1.3h-2.51v2.62h2.51A1.32,1.32,0,0,0,156.88,47Zm-.14,5.68-1.87-3.08h-2V52.7h-1.49V44.44h4.19A2.56,2.56,0,0,1,158.41,47a2.35,2.35,0,0,1-2,2.43l2.08,3.24Z"
                                  />
                                  <polygon
                                    id="Fill-34"
                                    className="cls-2"
                                    points="178.41 52.7 173.99 46.8 173.99 52.7 172.5 52.7 172.5 44.44 174.02 44.44 178.36 50.16 178.36 44.44 179.85 44.44 179.85 52.7 178.41 52.7"
                                  />
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                    </div>
                    <div className="flex flex-col flex-1 justify-center">
                      <h3 className="modal__store-name font-3xl mb-2 uppercase font-nromal tracking-widest">
                        {getStoreName(store.name)}
                      </h3>
                      <div
                        dangerouslySetInnerHTML={{ __html: serialize(store.address) }}
                        className="modal__store-list-item-address text-base font-light text-black mb-1"
                      ></div>
                      <a
                        className="text-base font-light text-black hover:text-gray-700 mb-1 underline hover:no-underline"
                        href={`tel:${store.phone.replace(/\s+/g, "")}`}
                      >
                        PH: {store.phone}
                      </a>
                      <button
                        onClick={() => handleStoreMap(index)}
                        className="Details__link font-bold font-base underline text-left flex gap-0 items-center"
                        data-store-index={index}
                      >
                        View store details <ChevronRight width={12} />
                      </button>
                    </div>
                    <div className="modal__store-stock w-1/4 sm:w-full sm:text-left flex justify-center sm:justify-start items-center text-base font-bold text-gray-600">
                      {store.status !== "" && store.status}
                    </div>
                  </div>
                );
              })}
            </div>
            {mapOpen && (
              <StoreMap
                name={activeStore.name}
                address={activeStore.address}
                phone={activeStore.phone}
                hours={activeStore.hours}
                distance={activeStore.distance.toFixed(2)}
                direction={activeStore.direction}
                mapOpen={mapOpen}
                setMapOpen={setMapOpen}
                lat={activeStore.latitude}
                lng={activeStore.longitude}
              />
            )}
          </main>
          <div className="modal__store-list"></div>
        </div>
      </div>
    </>
  );
};

export default FindInStore;
